import {
  Appliance,
  ApplianceConfiguration,
  ApplianceFilter,
  ApplianceInputInfo,
  ApplianceOutputInfo,
  AppliancePhysicalPortInfo,
  ApplianceType,
  BundleVersions,
  GeoApplianceResult,
  Group,
  ListApplianceSortableField,
  ListResult,
  NdiSource,
  Pcap,
  SortOrder,
  UpdateAppliancePayload,
} from 'common/api/v1/types'
import { getApplianceOwnerId } from '../../utils'
import {
  AppliancesRequestParams,
  EnrichedAppliance,
  EnrichedApplianceWithOwner,
  PaginatedRequestParams,
  singleSortQueryFromPaginatedRequestParams,
} from '../nm-types'
import { EdgeClient } from 'common/generated/edgeClient'
import { Query } from 'common/query'

export class AppliancesApi {
  constructor(private readonly edgeClient: EdgeClient) {}

  listAppliancePcaps(id: string): Promise<ListResult<Pcap>> {
    return this.edgeClient.listAppliancePcaps(id)
  }

  listApplianceNdiSources(id: string): Promise<ListResult<NdiSource>> {
    return this.edgeClient.listApplianceNdiSources(id)
  }

  recreateTunnels(id: string): Promise<Appliance> {
    return this.edgeClient.recreateApplianceTunnels(id)
  }

  deletePcap(applianceId: string, pcapId: string): Promise<Pcap[]> {
    return this.edgeClient.deleteAppliancePcap(applianceId, pcapId) as any
  }

  removeAppliance(id: string): Promise<Pick<Appliance, 'id'>> {
    return this.edgeClient.deleteAppliance(id)
  }
  updateAppliance(id: Appliance['id'], values: UpdateAppliancePayload): Promise<Appliance> {
    return this.edgeClient.updateAppliance(id, values)
  }

  /**
   * Getting appliance populating it with full owner object and physical ports' owners as well
   * @param id - appliance Id
   */
  async getAppliance(id: string): Promise<EnrichedAppliance> {
    const appliance = await this.edgeClient.getAppliance(id)
    const applianceOwnerId = getApplianceOwnerId(appliance)
    const groupIds = Array.from(new Set([applianceOwnerId, ...appliance.physicalPorts.map((p) => p.owner)]))
    const groups = (await this.edgeClient.listGroups({ filter: { ids: groupIds } })).items
    const enrichedAppliance = {
      ...appliance,
      owner: groups.find(({ id }) => id === applianceOwnerId) as Group,
      _physicalPorts: appliance.physicalPorts.map((port) => ({
        ...port,
        _owner: groups.find(({ id }) => id === port.owner) as Group,
      })),
    }
    return enrichedAppliance
  }

  /**
   * Getting ListResult of appliances populating each item with owner group object
   * @param requestParams
   */
  async getAppliances(requestParams: AppliancesRequestParams): Promise<ListResult<EnrichedApplianceWithOwner>> {
    const applianceList = await this.getBareAppliances(requestParams)
    if (applianceList.items.length === 0) {
      return { ...applianceList, items: [] }
    }

    const groupIds = applianceList.items.reduce<Array<string>>((acc, a) => {
      const id = typeof a.owner === 'string' ? a.owner : a.owner.id
      if (acc.includes(id)) return acc
      return acc.concat(id)
    }, [])
    const groups = (await this.edgeClient.listGroups({ filter: { ids: groupIds } })).items
    const groupsMap = groups.reduce<{ [key: string]: Group }>((acc, item) => ({ ...acc, [item.id]: item }), {})

    return {
      ...applianceList,
      items: applianceList.items.map((a) => ({
        ...a,
        _owner: groupsMap[typeof a.owner === 'string' ? a.owner : a.owner.id],
      })),
    }
  }

  getBareAppliances({
    owner: groupId,
    groupName,
    filter: searchName,
    types,
    regions,
    tags,
    ...params
  }: AppliancesRequestParams): Promise<ListResult<Appliance>> {
    const filter: ApplianceFilter = {
      group: groupId,
      groupName,
      searchName,
      regionNames: regions?.split(','),
      types: types?.split(',') as ApplianceType[] | undefined,
      tags: tags?.split(','),
    }
    const query = singleSortQueryFromPaginatedRequestParams({ filter, paginatedRequestParams: params })
    return this.listAppliances(query)
  }

  listAppliances(
    query?: Query<ApplianceFilter, SortOrder<ListApplianceSortableField>>,
  ): Promise<ListResult<Appliance>> {
    return this.edgeClient.listAppliances(query)
  }

  listRegisteredApplianceTypes(): Promise<ListResult<ApplianceType>> {
    return this.edgeClient.listApplianceRegisteredTypes()
  }

  /**
   * Returns appliances with coordination to show on map
   * @param paginatedRequestParams
   */
  getGeoAppliances(paginatedRequestParams: PaginatedRequestParams): Promise<GeoApplianceResult> {
    const query = singleSortQueryFromPaginatedRequestParams({ filter: undefined, paginatedRequestParams })
    return this.edgeClient.getGeoAppliance(query)
  }

  /**
   * Command to restart appliance
   * @param id
   */
  restart(id: string, requestedVersion?: string): Promise<Appliance> {
    return this.edgeClient.restartAppliance(id, { requestedVersion }, { timeoutMs: 180_000 })
  }

  /**
   * Command to upgrade image for appliance
   * @param id
   */
  upgradeImage(id: string, imageVersion: string): Promise<void> {
    return this.edgeClient.upgradeImageAppliance(id, { imageVersion })
  }

  /**
   * Returns appliance configuration for super user
   * @param id
   */
  getConfig(id: string): Promise<ApplianceConfiguration> {
    return this.edgeClient.getApplianceConfig(id, '')
  }

  listApplianceInputs(id: Appliance['id'], params: PaginatedRequestParams): Promise<ListResult<ApplianceInputInfo>> {
    const query = singleSortQueryFromPaginatedRequestParams({
      filter: undefined,
      paginatedRequestParams: params,
    })
    return this.edgeClient.listApplianceInputs(id, query)
  }

  listApplianceOutputs(id: Appliance['id'], params: PaginatedRequestParams): Promise<ListResult<ApplianceOutputInfo>> {
    const query = singleSortQueryFromPaginatedRequestParams({
      filter: undefined,
      paginatedRequestParams: params,
    })
    return this.edgeClient.listApplianceOutputs(id, query)
  }

  async listAppliancePhysicalPorts(id: Appliance['id']): Promise<AppliancePhysicalPortInfo> {
    return this.edgeClient.getAppliancePorts(id)
  }

  clearCachedApplianceConfig(id: Appliance['id']) {
    return this.edgeClient.clearApplianceConfig(id)
  }

  listApplianceCommands(id: Appliance['id']) {
    return this.edgeClient.listApplianceCommands(id)
  }

  getApplianceSoftwareVersions(): Promise<BundleVersions> {
    return this.edgeClient.getApplianceSoftwareVersions()
  }
}
